import { Fragment } from 'react';
import { CreditCard, Mail, Phone, UserCircle } from 'lucide-react';
import { formatMoney } from '../../utils/format';

/**
 * A ConnectWise quote as the customer reads it, from the server's
 * `presentation` (services/quotes/presentation.js): sections with their own
 * subtotals, part numbers, recurring charges apart from what is due now,
 * shipping, and optional lines shown but not counted. Used by the shared link
 * and the signed-in quote page, so the two cannot disagree about a total.
 */
const money = (v) => formatMoney(Number(v) || 0);
/** "Recurring (Annual)", or "Recurring" for a line whose cycle ConnectWise does not record. */
const recurringName = (cycle) => (cycle && cycle !== 'Recurring' ? `Recurring (${cycle})` : 'Recurring');

/** Each billing cycle's recurring total; a quote can mix Monthly and Annual lines. */
export function recurringParts(totals) {
  if (!(totals?.recurring > 0)) return [];
  if (totals.recurringByCycle?.length) return totals.recurringByCycle;
  return [{ label: totals.recurringLabel || 'Recurring', amount: totals.recurring }];
}

/** "$199.00 monthly and $239.96 annual" (tax included), for a sentence after "plus". */
export function RecurringPhrase({ totals, strong = false }) {
  const parts = recurringParts(totals);
  return parts.map((p, i) => (
    <span key={p.label}>
      {i > 0 && (i === parts.length - 1 ? ' and ' : ', ')}
      <span className={strong ? 'font-semibold' : ''}>{money(p.total ?? p.amount)}</span> {p.label === 'Recurring' ? 'recurring' : p.label.toLowerCase()}
      {p.tax > 0 && <> including {money(p.tax)} tax</>}
    </span>
  ));
}

function LineRow({ line }) {
  const meta = [line.partNumber, line.manufacturer].filter(Boolean).join(' · ');
  return (
    <tr className={line.optional ? 'bg-amber-50 dark:bg-amber-900/20' : ''}>
      <td className="px-6 py-3 align-top">
        <div className="font-medium text-neutral-900 dark:text-white">
          {line.name}
          {line.optional && (
            <span className="ml-2 text-xs bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 px-2 py-0.5 rounded-full">Not included</span>
          )}
        </div>
        {meta && <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 tabular-nums">{meta}</div>}
        {line.description && <div className="text-sm text-neutral-600 dark:text-neutral-300 mt-1 whitespace-pre-line">{line.description}</div>}
        {line.cycles && <div className="text-xs text-primary-600 dark:text-primary-400 mt-1">For {line.cycles} billing cycles</div>}
      </td>
      <td className="px-4 py-3 text-center text-neutral-700 dark:text-neutral-300 align-top tabular-nums">{line.quantity}</td>
      <td className="px-4 py-3 text-right text-neutral-700 dark:text-neutral-300 align-top tabular-nums">{money(line.unitPrice)}</td>
      <td className="px-6 py-3 text-right font-medium text-neutral-900 dark:text-white align-top tabular-nums">{money(line.extendedPrice)}</td>
    </tr>
  );
}

function SummaryRow({ label, value, strong, accent }) {
  return (
    <tr className={strong ? 'bg-neutral-50 dark:bg-neutral-900/50' : ''}>
      <td colSpan="3" className={`px-6 ${strong ? 'py-4 font-medium text-neutral-900 dark:text-white' : 'py-2 text-neutral-600 dark:text-neutral-300'} text-right`}>{label}</td>
      <td className={`px-6 ${strong ? 'py-4 text-xl font-bold text-primary-600 dark:text-primary-400' : `py-2 font-medium ${accent || 'text-neutral-900 dark:text-white'}`} text-right tabular-nums`}>{value}</td>
    </tr>
  );
}

export function QuoteLineSections({ presentation, depositAmount = 0, labels = {} }) {
  const { sections = [], optional = [], showSectionTitles, totals = {} } = presentation || {};
  const l = { item: 'Item', qty: 'Qty', price: 'Unit Price', total: 'Total', ...labels };
  const hasRecurring = totals.recurring > 0;
  const hasOneTime = sections.some((s) => !s.recurring);
  const head = 'px-4 py-3 text-sm font-medium text-neutral-600 dark:text-neutral-400';

  if (!sections.length && !optional.length) {
    return <div className="p-8 text-center text-neutral-500 dark:text-neutral-400">No line items in this quote</div>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="bg-neutral-50 dark:bg-neutral-900/50 border-b border-neutral-200 dark:border-neutral-700">
            <th className={`${head} pl-6 text-left`}>{l.item}</th>
            <th className={`${head} text-center`}>{l.qty}</th>
            <th className={`${head} text-right`}>{l.price}</th>
            <th className={`${head} pr-6 text-right`}>{l.total}</th>
          </tr>
        </thead>
        {sections.map((section) => (
          <tbody key={section.key} className="divide-y divide-neutral-100 dark:divide-neutral-700 border-b border-neutral-200 dark:border-neutral-700">
            {showSectionTitles && (
              <tr className="bg-neutral-50/60 dark:bg-neutral-900/30">
                <td colSpan="4" className="px-6 pt-4 pb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">{section.title}</td>
              </tr>
            )}
            {section.lines.map((line, i) => <LineRow key={line.id ?? i} line={line} />)}
            {showSectionTitles && (
              <tr>
                <td colSpan="3" className="px-6 py-2 text-right text-sm text-neutral-500 dark:text-neutral-400">
                  {section.recurring ? `${section.cycleLabel || totals.recurringLabel || 'Recurring'} subtotal` : `${section.title} subtotal`}
                </td>
                <td className="px-6 py-2 text-right text-sm font-medium text-neutral-700 dark:text-neutral-200 tabular-nums">{money(section.subtotal)}</td>
              </tr>
            )}
          </tbody>
        ))}
        {optional.length > 0 && (
          <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700 border-b border-neutral-200 dark:border-neutral-700">
            <tr>
              <td colSpan="4" className="px-6 pt-4 pb-2 text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400">Optional items — not in the total</td>
            </tr>
            {optional.map((line, i) => <LineRow key={line.id ?? `o${i}`} line={line} />)}
          </tbody>
        )}
        <tfoot>
          {hasOneTime && <SummaryRow label={hasRecurring ? 'One-time subtotal' : 'Subtotal'} value={money(totals.oneTime)} />}
          {totals.shipping > 0 && <SummaryRow label="Shipping" value={money(totals.shipping)} />}
          {totals.tax > 0 && <SummaryRow label="Tax" value={money(totals.tax)} />}
          {(hasOneTime || totals.shipping > 0) && <SummaryRow label={hasRecurring ? 'Total due now' : 'Total'} value={money(totals.total)} strong />}
          {recurringParts(totals).map((p) => (p.tax > 0 ? (
            <Fragment key={p.label}>
              <SummaryRow label={`${recurringName(p.label)} subtotal`} value={money(p.amount)} />
              <SummaryRow label={p.label === 'Recurring' ? 'Recurring tax' : `Tax (${p.label})`} value={money(p.tax)} />
              <SummaryRow label={`${recurringName(p.label)} total`} value={money(p.total)} strong={!hasOneTime} accent="text-primary-600 dark:text-primary-400" />
            </Fragment>
          ) : (
            <SummaryRow key={p.label} label={recurringName(p.label)} value={money(p.amount)} strong={!hasOneTime} accent="text-primary-600 dark:text-primary-400" />
          )))}
          {depositAmount > 0 && (
            <tr className="border-t border-neutral-200 dark:border-neutral-700">
              <td colSpan="3" className="px-6 py-3 text-right text-neutral-600 dark:text-neutral-300">
                <span className="flex items-center justify-end gap-2"><CreditCard className="w-4 h-4" /> Deposit Required</span>
              </td>
              <td className="px-6 py-3 text-right font-semibold text-amber-600 dark:text-amber-400 tabular-nums">{money(depositAmount)}</td>
            </tr>
          )}
        </tfoot>
      </table>
    </div>
  );
}

/** The opportunity's sales rep, with office contact details. */
export function SalesRepCard({ rep, className = '' }) {
  if (!rep) return null;
  return (
    <div className={`bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-5 ${className}`}>
      <div className="text-xs font-semibold uppercase tracking-wider text-neutral-500 dark:text-neutral-400 mb-3">Your account manager</div>
      <div className="flex items-start gap-3">
        <UserCircle className="w-10 h-10 text-neutral-300 dark:text-neutral-600 flex-shrink-0" />
        <div className="min-w-0">
          <div className="font-semibold text-neutral-900 dark:text-white">{rep.name}</div>
          {rep.title && <div className="text-sm text-neutral-600 dark:text-neutral-300">{rep.title}</div>}
          {rep.email && (
            <a href={`mailto:${rep.email}`} className="mt-2 flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline break-all">
              <Mail className="w-4 h-4 flex-shrink-0" /> {rep.email}
            </a>
          )}
          {rep.phone && (
            <a href={`tel:${rep.phone}`} className="mt-1 flex items-center gap-2 text-sm text-primary-600 dark:text-primary-400 hover:underline">
              <Phone className="w-4 h-4 flex-shrink-0" /> {rep.phone}
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

/** The MSP's own words about itself, from Quotes → Settings. Plain text, paragraphs kept. */
export function AboutCard({ about, fallbackTitle = 'About us', className = '' }) {
  if (!about?.text) return null;
  return (
    <div className={`bg-white dark:bg-neutral-800 rounded-xl shadow-soft border border-neutral-200 dark:border-neutral-700 p-6 ${className}`}>
      <h2 className="font-semibold text-neutral-900 dark:text-white mb-3">{about.title || fallbackTitle}</h2>
      <div className="space-y-3 text-sm text-neutral-700 dark:text-neutral-300 leading-relaxed">
        {about.text.split(/\n\s*\n/).map((p, i) => <p key={i} className="whitespace-pre-line">{p}</p>)}
      </div>
    </div>
  );
}

/** The purchase order box on an accept dialog. Renders nothing when the MSP turned it off. */
export function PurchaseOrderField({ mode, value, onChange, inputClassName = 'input w-full' }) {
  if (!mode || mode === 'off') return null;
  return (
    <div>
      <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
        Purchase order number{mode === 'required' ? ' *' : ' (optional)'}
      </label>
      <input
        type="text"
        className={inputClassName}
        maxLength={50}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        required={mode === 'required'}
      />
    </div>
  );
}
